import type { components } from '@multaqa/api-client'
import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { subtractDecimalStrings } from '@/lib/decimal'
import { invoicesKey, unwrap, useFreshCompanyId } from './invoice-api-helpers'
import type {
  CreditNoteBody,
  CustomerOpenDocuments,
  Invoice,
  InvoiceTotalsPreview,
} from './invoice-api-types'

export function useCreateCreditNote(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<Invoice, Error, { invoiceId: string; body: CreditNoteBody }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<Invoice, Error, { invoiceId: string; body: CreditNoteBody }>({
    ...options,
    mutationFn: async ({ invoiceId, body }) => {
      const response = await api.POST('/accounting/invoices/{invoice}/credit-notes', {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { invoice: invoiceId } },
        body: body as components['schemas']['StoreCreditNoteRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to create credit note')
      return unwrap<Invoice>(response.data ?? {}, 'invoices')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: invoicesKey(companyId) })
      options?.onSuccess?.(...args)
    },
  })
}

export function usePreviewCreditNoteTotals(companyId: string | null) {
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<InvoiceTotalsPreview, Error, { invoiceId: string; body: CreditNoteBody }>({
    mutationFn: async ({ invoiceId, body }) => {
      const response = await api.POST(
        '/accounting/invoices/{invoice}/credit-notes/totals-preview',
        {
          headers: { 'X-Company-Id': currentCompany() },
          params: { path: { invoice: invoiceId } },
          body: body as components['schemas']['StoreCreditNoteTotalsPreviewRequest'],
        },
      )
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to preview credit note totals')
      }
      return unwrap<InvoiceTotalsPreview>(response.data ?? {}, 'credit note totals preview')
    },
  })
}

export function useApplyCreditNote(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      creditNoteId,
      body,
    }: {
      creditNoteId: string
      body: { applied_on: string; allocations: { invoice_id: string; amount: string }[] }
    }) => {
      if (!companyId) throw new Error('Company required')
      const response = await api.POST('/accounting/credit-notes/{creditNote}/applications', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { creditNote: creditNoteId } },
        body,
      })
      if (response.error) throw new Error(response.error.message ?? 'Could not apply credit')
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoicesKey(companyId) })
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'customers'],
      })
    },
  })
}

export function useUndoCreditApplication(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (applicationId: string) => {
      if (!companyId) throw new Error('Company required')
      const response = await api.DELETE('/accounting/credit-note-applications/{application}', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { application: applicationId } },
      })
      if (response.error) throw new Error(response.error.message ?? 'Could not undo application')
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: invoicesKey(companyId) }),
  })
}

/** f427: apply a confirmed payment's on-account money to an invoice. */
export function useApplyPaymentCredit(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      paymentId,
      body,
    }: {
      paymentId: string
      body: { invoice_id: string; amount: string; applied_on: string }
    }) => {
      if (!companyId) throw new Error('Company required')
      const response = await api.POST('/accounting/payments/{payment}/applications', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { payment: paymentId } },
        body,
      })
      if (response.error) throw new Error(response.error.message ?? 'Could not apply payment')
      return response.data
    },
    onSuccess: (_data, { paymentId, body }) => {
      // Take the applied money off the cached list at once, so the row
      // cannot be applied twice while the refetch below is in flight.
      queryClient.setQueriesData<CustomerOpenDocuments>(
        {
          queryKey: ['companies', companyId, 'accounting', 'customers'],
          predicate: (query) => query.queryKey[5] === 'open-documents',
        },
        (documents) =>
          documents && {
            ...documents,
            payments: documents.payments.map((payment) =>
              payment.id === paymentId
                ? {
                    ...payment,
                    unallocated_amount: subtractDecimalStrings(
                      payment.unallocated_amount,
                      body.amount,
                    ),
                  }
                : payment,
            ),
          },
      )
      void queryClient.invalidateQueries({ queryKey: invoicesKey(companyId) })
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'customers'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'payments'],
      })
    },
  })
}
